<script setup lang="ts">
import type { ShipmentMetrics } from '@/types/shipments';
import { SHIPMENT_DISPLAY_STATUS, SHIPMENT_QUEUE_FILTER } from '@/utils/shipment-status-ui';

defineProps<{
  metrics: ShipmentMetrics;
  activeMetricFilter?: string | null;
}>();

const emit = defineEmits<{
  (e: 'card-click', key: string): void;
}>();

const cards = [
  {
    label: 'Active Shipments',
    key: 'active_shipments',
    iconColor: 'text-green-500',
  },
  { label: 'Pickup Today', key: 'picked_up_today', iconColor: 'text-blue-500' },
  {
    label: 'Delivery Today',
    key: 'delivery_today',
    iconColor: 'text-purple-500',
  },
  { label: 'Late Delivery', key: 'late_delivery', iconColor: 'text-red-500' },
] as const;

function getCardValue(key: string, metrics: ShipmentMetrics): number {
  if (key === 'active_shipments')
    return metrics[SHIPMENT_DISPLAY_STATUS.Active];
  if (key === 'picked_up_today') return metrics.picked_up_today;
  if (key === 'delivery_today') return metrics.delivery_today;
  if (key === 'late_delivery') return metrics[SHIPMENT_QUEUE_FILTER.Late];
  return 0;
}
</script>

<template>
  <div class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
    <div
      v-for="card in cards"
      :key="card.key"
      class="cursor-pointer rounded-lg border px-4 py-3 shadow-sm transition-shadow duration-200 hover:shadow-md"
      :class="
        activeMetricFilter === card.key
          ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-400'
          : 'border-gray-200 bg-white'
      "
      @click="emit('card-click', card.key)"
    >
      <div class="mb-1 flex items-center justify-between">
        <span class="text-sm font-medium text-gray-500">{{ card.label }}</span>
        <!-- Active Shipments -->
        <svg
          v-if="card.key === 'active_shipments'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="size-5"
          :class="card.iconColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
            clip-rule="evenodd"
          />
        </svg>
        <!-- Picked Up Today -->
        <svg
          v-else-if="card.key === 'picked_up_today'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="size-5"
          :class="card.iconColor"
        >
          <path
            d="M6.5 3c-1.051 0-2.093.04-3.125.117A1.49 1.49 0 0 0 2 4.607V10.5h9V4.606c0-.771-.59-1.43-1.375-1.489A41.568 41.568 0 0 0 6.5 3ZM2 12v2.5A1.5 1.5 0 0 0 3.5 16h.041a3 3 0 0 1 5.918 0h.791a.75.75 0 0 0 .75-.75V12H2Z"
          />
          <path
            d="M6.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM13.25 5a.75.75 0 0 0-.75.75v8.514a3.001 3.001 0 0 1 4.893 1.44c.37-.275.61-.719.595-1.227a24.905 24.905 0 0 0-1.784-8.549A1.486 1.486 0 0 0 14.823 5H13.25ZM14.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
          />
        </svg>
        <!-- Delivery Today -->
        <svg
          v-else-if="card.key === 'delivery_today'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="size-5"
          :class="card.iconColor"
        >
          <path
            fill-rule="evenodd"
            d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z"
            clip-rule="evenodd"
          />
        </svg>
        <!-- Late Delivery -->
        <svg
          v-else-if="card.key === 'late_delivery'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="size-5"
          :class="card.iconColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <span class="text-2xl font-bold text-gray-900">{{
        getCardValue(card.key, metrics)
      }}</span>
    </div>
  </div>
</template>
